/**
 * The trains heard: each of the nearest few rolls (`train:roll`) and drives
 * (`train:motor:*`, an electric unit's whine or a diesel's beat), both
 * placed where the train passes nearest the player and played faster and
 * louder the faster it goes. Its wheels clack over the rail joints, each
 * bogie near the player two knocks, one an axle after the other, as each
 * joint comes; it sounds its horn coming up on the player, its brakes hiss
 * as it comes to a stand at a station, and its doors chime as they close.
 */

import type { At, SoundSystem } from './system.js';
import { place } from './system.js';

/** A train as the sound needs it: see `Trains.heard`. */
export interface HeardTrain {
  id: string;
  /** Its nearest point to the player. */
  at: At;
  /** m/s. */
  speed: number;
  electric: boolean;
  /** How far its front has gone, metres: for when it comes to a rail joint. */
  odometer: number;
  /** Each bogie's place, and how far behind the front it is, metres. */
  bogies: Array<{ at: At; behind: number }>;
}

/** The trains heard at once, and how far, metres. */
const VOICES = 3;
const HEARD = 900;
/** The rail's lengths between joints, and a bogie's two axles' spacing, metres. */
const JOINT = 26;
const AXLES = 2.6;
/** Bogies near the player heard clacking, at most, and how near, metres. */
const CLACKING = 8;
const CLACK_NEAR = 90;

interface Voice {
  roll: NonNullable<ReturnType<SoundSystem['loopAt']>>;
  motor: NonNullable<ReturnType<SoundSystem['loopAt']>>;
  joint: number;
}

export class TrainSounds {
  private readonly voices = new Map<string, Voice>();
  private variant = 0;

  constructor(private readonly sound: SoundSystem) {}

  /** Each frame: the trains about, and where the player is. */
  update(trains: readonly HeardTrain[], listener: At): void {
    const ctx = this.sound.context;
    const near = trains
      .map(t => ({ t, d: Math.hypot(t.at.x - listener.x, t.at.y - listener.y, t.at.z - listener.z) }))
      .filter(n => n.d < HEARD)
      .sort((a, b) => a.d - b.d)
      .slice(0, VOICES);
    const keep = new Set(near.map(n => n.t.id));
    for (const [id, v] of this.voices) {
      if (keep.has(id) && this.sound.live) continue;
      v.roll.source.stop();
      v.motor.source.stop();
      this.voices.delete(id);
    }
    if (ctx === undefined || !this.sound.live) return;
    const now = ctx.currentTime;
    for (const { t } of near) {
      let v = this.voices.get(t.id);
      if (v === undefined) {
        const roll = this.sound.loopAt('train:roll', 'world', t.at, 14);
        const motor = this.sound.loopAt(t.electric ? 'train:motor:electric' : 'train:motor:diesel', 'world', t.at, 10);
        if (roll === undefined || motor === undefined) { roll?.source.stop(); motor?.source.stop(); continue; }
        v = { roll, motor, joint: Math.floor(t.odometer / JOINT) };
        this.voices.set(t.id, v);
      }
      const pace = Math.min(1, t.speed / 30);
      place(v.roll.panner, t.at, now);
      place(v.motor.panner, t.at, now);
      v.roll.gain.gain.setTargetAtTime(0.9 * Math.pow(pace, 1.1), now, 0.2);
      v.roll.source.playbackRate.setTargetAtTime(0.55 + 0.6 * pace, now, 0.3);
      if (t.electric) {
        v.motor.gain.gain.setTargetAtTime(0.06 + 0.22 * pace, now, 0.3);
        v.motor.source.playbackRate.setTargetAtTime(0.35 + 1.1 * pace, now, 0.3);
      } else {
        v.motor.gain.gain.setTargetAtTime(0.3 + 0.4 * pace, now, 0.3);
        v.motor.source.playbackRate.setTargetAtTime(0.7 + 0.8 * pace, now, 0.3);
      }
      this.clack(t, v, listener);
    }
  }

  /** Its front at a new rail joint: the bogies near the player each knock over it as they come to it. */
  private clack(t: HeardTrain, v: Voice, listener: At): void {
    const joint = Math.floor(t.odometer / JOINT);
    if (joint === v.joint) return;
    v.joint = joint;
    if (t.speed < 2) return;
    const gain = Math.min(1, 0.25 + t.speed / 30);
    const close = t.bogies
      .map(b => ({ b, d: Math.hypot(b.at.x - listener.x, b.at.z - listener.z) }))
      .filter(n => n.d < CLACK_NEAR)
      .sort((a, b) => a.d - b.d)
      .slice(0, CLACKING);
    for (const { b } of close) {
      // The joint the front just crossed reaches this bogie when it has gone its distance behind, a joint at a time.
      const delay = (b.behind % JOINT) / t.speed;
      for (const axle of [0, AXLES]) {
        this.sound.play(`train:clack:${this.variant++ % 2}`, {
          bus: 'world', at: b.at, gain, rate: 0.95 + 0.1 * Math.random(), delay: delay + axle / t.speed, near: 6,
        });
      }
    }
  }

  /** A train's one-off sound (`Trains.takeSounds`): its horn, its brakes, its doors; from `at`. */
  event(id: string, at: At): void {
    const loud = id === 'train:horn' ? { gain: 1, near: 40 } : id === 'airbrake' ? { gain: 0.7, near: 10 } : { gain: 0.6, near: 8 };
    this.sound.play(id, { bus: 'world', at, ...loud });
  }
}
